import React, { FunctionComponent, useState, ChangeEvent, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';
import { FormattedMessage } from 'react-intl';
import { FormControl } from 'react-bootstrap';
import RegularButton from '../RegularButton';
import { IBindingCallback1 } from '../../common/models/callback';
import { ReactComponent as Exclamation } from '../../assets/icons/exclamation.svg';
import { ReactComponent as ArrowRight } from '../../assets/icons/arrow-right.svg';
import { handleEnter } from '../../common/helpers/keyboardHelper';
import LoaderWrapper from '../LoaderWrapper';
import styles from './styles.module.scss';

interface IIntroPageProps {
  errorCode: string;
  isChecking: boolean;
  onStart: IBindingCallback1<string>;
}

const regexp = /^[0-9\b]+$/;

const CheckCode: FunctionComponent<IIntroPageProps> = ({ errorCode, isChecking, onStart }) => {
  const [code, setCode] = useState('');
  const [invalidCode, setInvalidCode] = useState(false);

  const onEnterCode = ({ currentTarget: { value } }: ChangeEvent<HTMLInputElement>) => {
    if (value === '' || regexp.test(value)) {
      setCode(value);
      if (invalidCode) {
        setInvalidCode(false);
      }
    }
  };

  useEffect(() => {
    if (errorCode === 'Authorization code is invalid') {
      setInvalidCode(true);
    }
  }, [errorCode]);

  const inactiveUserError = errorCode === 'User is inactive';
  const showError = invalidCode || inactiveUserError;

  const onSubmit = () => {
    if (code.length === 4) {
      onStart(code);
    }
  };

  const keyHandler = (event: KeyboardEvent) => handleEnter(event, onSubmit);

  const swipeHandler = useSwipeable({
    onSwipedLeft: onSubmit,
    trackMouse: false
  });

  useEffect(() => {
    window.addEventListener('keydown', keyHandler);
    return () => {
      window.removeEventListener('keydown', keyHandler);
    };
  }, [keyHandler]);

  return (
    <div {...swipeHandler} className="flex-column survey-page">
      <LoaderWrapper loading={isChecking} transparent>
        <div className="cf-title mb-2">
          <FormattedMessage
            id="candidateForm.code.title"
            defaultMessage="Before we start..."
          />
        </div>
        <div className="cf-subtitle text-center">
          <FormattedMessage
            id="candidateForm.enterCode"
            defaultMessage="For security purposes, please enter the code that was sent to you."
          />
        </div>
        <FormControl
          onChange={onEnterCode}
          bsPrefix={`highlighted-input ${styles.codeInput} ${showError && styles.error}`}
          value={code}
          aria-describedby="basic-addon1"
          type="tel"
          placeholder="1234"
        />
        {invalidCode && (
          <div className="d-flex">
            <span className="exclamation-mark mr-2"><Exclamation /></span>
            <span className={styles.errorLabel}>
              <FormattedMessage
                id="candidateForm.wrongCode"
                defaultMessage="Hmmm that code didn't work. Please try again."
              />
            </span>
          </div>
        )}
        {inactiveUserError && (
          <div className="d-flex">
            <span className="exclamation-mark mr-2"><Exclamation /></span>
            <span className={styles.errorLabel}>
              <FormattedMessage
                id="candidateForm.inactiveUser"
                defaultMessage={`This page is no longer active. Please contact the hiring organization 
                with any questions`}
              />
            </span>
          </div>
        )}
        <div className="cf-footer-btns justify-content-center mt-4">
          <RegularButton
            disabled={code.length !== 4}
            size="lg"
            textIntlId="candidateForm.next"
            defaultMessage="Next"
            icon={<ArrowRight className={styles.arrow} />}
            iconPosition="right"
            onClick={() => onStart(code)}
          />
        </div>
      </LoaderWrapper>
    </div>
  );
};

export default CheckCode;
